import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Grid, TextField, Typography, Button } from '@mui/material';
import { Clear } from '@mui/icons-material';

export interface FeedbackFilters {
  userId?: string;
  noteId?: string;
  moduleId?: string;
  startDate?: string;
  endDate?: string;
}

interface FeedbackSessionFiltersProps {
  filters: FeedbackFilters;
  onChange: (filters: FeedbackFilters) => void;
  onClear: () => void;
}

export function FeedbackSessionFilters({
  filters,
  onChange,
  onClear,
}: FeedbackSessionFiltersProps) {
  const [localFilters, setLocalFilters] = useState<FeedbackFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof FeedbackFilters, value: string) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    const newFilters = { ...localFilters, startDate, endDate };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== undefined).length;

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Filters</Typography>
          {activeFilterCount > 0 && (
            <Button size="small" startIcon={<Clear />} onClick={onClear} variant="outlined">
              Clear All
            </Button>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              label="User ID"
              size="small"
              value={localFilters.userId || ''}
              onChange={e => handleFilterChange('userId', e.target.value)}
              placeholder="Filter by user/provider..."
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              label="Note ID"
              size="small"
              value={localFilters.noteId || ''}
              onChange={e => handleFilterChange('noteId', e.target.value)}
              placeholder="Filter by note..."
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              label="Module ID"
              size="small"
              value={localFilters.moduleId || ''}
              onChange={e => handleFilterChange('moduleId', e.target.value)}
              placeholder="Filter by module..."
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              size="small"
              value={localFilters.startDate || ''}
              onChange={e => handleDateChange(e.target.value, localFilters.endDate || '')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              size="small"
              value={localFilters.endDate || ''}
              onChange={e => handleDateChange(localFilters.startDate || '', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
